import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { getAtBatState } from '../store/selectors/getAtBat';
import { useSelector } from 'react-redux';
import { PitchByPitchMobileWrapper, LiveFeedMobileWrapper, InfoDivider, PlayDetailMediaWrapper, ControlledOverflowOverride } from '../atbat.style';
import AtBatVideo from './AtBatVideo';
import AtBatPitchFxDataContainer from '../containers/AtBatPitchFxDataContainer';
import LiveFeedPlays from '@gameday/components/LiveFeed/liveFeed/LiveFeedPlays';
import GamedayFieldVisionPlayer from '@gameday/components/FieldVision/components/GamedayFieldVisionPlayer';
import { FvodApp } from '@gameday/components/FieldVision/fieldvision.style';
import FieldVisionLogin from './FieldVisionLogin';
import useNativeLoginFunc from '@gameday/utils/hooks/useNativeLoginFunc';

import {
    Description,
    Event,
    Matchup,
    Probability,
    Statcast
} from '../summary';
import DetailSituation from '../summary/DetailSituation';

const atBatPitchFxConfig = {
    batterScaling: true,
    tiltShift: false,
    base_width: 1158,
    base_height: 869,
    fixed_width: 1080,
    hoverData: false,
    fontSize: 100,
    clip: {
        width: 363,
        height: 300
    },
    mode: '2d'
};

const MobileAtBatPlay = ({
    playIndex,
    onAuthOpen,
    setTutorialOpen
}) => {
    const [isOptedIn, setIsOptedIn] = useState(false);
    const [isFVFullscreen, setIsFVFullscreen] = useState(false);
    const {
        playData = {},
        isLoading,
        deviceInteraction,
        gamePk,
        teams,
        venueId,
        language,
        accessToken,
        isLoggedIn,
        fieldVisionODAvailable,
        forcedFieldVisionOn
    } = useSelector(getAtBatState(playIndex));

    const controllerRef = useRef();
    const nativeLoginFunc = useNativeLoginFunc();
    const { highlights, resultPlayGuid } = playData;

    const {
        about: {
            halfInning = '',
            inning,
            isTopInning,
            isScoringPlay
        } = {},
        matchup = {},
        playMetrics: {
            preCount = {},
            playEvents: metricsPlayEvents = [],
            awayTeamWinProbability,
            awayTeamWinProbabilityAdded,
            homeTeamWinProbability,
            homeTeamWinProbabilityAdded
        } = {},
        result: {
            event = '',
            description = '',
            awayScore,
            homeScore
        } = {},
        playEvents = []
    } = playData;

    const {
        hitData = {}
    } = playEvents?.[playEvents?.length - 1] || {};

    const {
        away = {},
        home = {}
    } = teams;

    const validLaunchAngle = (typeof hitData?.launchAngle !== 'undefined') || (hitData?.launchAngle !== null);
    const fvodDisplay = isLoggedIn && isOptedIn
        ? <FvodApp>
            <GamedayFieldVisionPlayer
                ref={controllerRef}
                gamePk={gamePk * 1}
                debug={false}
                accessToken={accessToken}
                loop={true}
                playId={resultPlayGuid}
                embeddedControls={'fvod'}
                defaultCam={'batter'}
                autoPlay={false}
                setIsFVFullscreen={setIsFVFullscreen}
                setTutorialOpen={setTutorialOpen}
                key={gamePk + accessToken}
            />
        </FvodApp>
        : <FieldVisionLogin venueId={venueId} isLoggedIn={isLoggedIn} setIsOptedIn={setIsOptedIn} onAuthOpen={nativeLoginFunc || onAuthOpen} />;

    return (
        <>
            <DetailSituation
                inning={inning}
                halfInning={halfInning}
                awayScore={awayScore}
                homeScore={homeScore}
                away={away}
                home={home}
                isMobile
                isTopInning={isTopInning}
                isScoringPlay={isScoringPlay}
            />
            <Event
                event={event}
            />
            {highlights
                ? <ControlledOverflowOverride fvodFullscreen={isFVFullscreen}
                    disableShadows
                    childWidth={375}
                    key={`game-${gamePk}-${resultPlayGuid}`}
                >
                    <PlayDetailMediaWrapper>
                        <AtBatVideo gamePk={gamePk} highlights={highlights} isLoading={isLoading} deviceInteraction={deviceInteraction} />
                    </PlayDetailMediaWrapper>
                    { (fieldVisionODAvailable || forcedFieldVisionOn)
                        ? <PlayDetailMediaWrapper>
                            {fvodDisplay}
                        </PlayDetailMediaWrapper>
                        : null }
                </ControlledOverflowOverride>
                : null}
            <Description
                description={description}
            />
            <Probability
                halfInning={halfInning}
                away={away}
                home={home}
                awayTeamWinProbability={awayTeamWinProbability}
                awayTeamWinProbabilityAdded={awayTeamWinProbabilityAdded}
                homeTeamWinProbability={homeTeamWinProbability}
                homeTeamWinProbabilityAdded={homeTeamWinProbabilityAdded}
                isMobile
            />
            <InfoDivider />
            { validLaunchAngle
                ? <>
                    <Statcast
                        isMobile
                        lastPlayEvent={{
                            hitData
                        }}
                    />
                    <InfoDivider />
                </>
                : null}
            <Matchup
                away={away}
                home={home}
                matchup={matchup}
                isMobile
                language={language}
                deviceInteraction={deviceInteraction}
                offense={metricsPlayEvents?.length && metricsPlayEvents[metricsPlayEvents.length - 1]?.offense}
                count={preCount}
            />
            <PitchByPitchMobileWrapper>
                <AtBatPitchFxDataContainer playIndex={playIndex} pitchFxConfig={atBatPitchFxConfig} />
            </PitchByPitchMobileWrapper>
            <LiveFeedMobileWrapper>
                <LiveFeedPlays playArr={[playIndex]} showAllPitches={true} showPitchesOnly={true} showBasesText={false} />
            </LiveFeedMobileWrapper>
        </>
    );
};

MobileAtBatPlay.propTypes = {
    playIndex: PropTypes.number.isRequired,
    onAuthOpen: PropTypes.func.isRequired,
    setTutorialOpen: PropTypes.func
};

export default MobileAtBatPlay;
